import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SxProps } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { UsageBreakdownPanel } from '../common';
import {
  hasUsageBreakdown,
  mergeUsageBreakdowns,
} from '../../utils/usageBreakdown';
import type { UsageBreakdown } from '../../utils/usageBreakdown';

type ConversationExecutionLike = {
  id?: string | number | null;
  status?: string | null;
  total_tokens?: number | string | null;
  total_cost?: number | string | null;
  usage_breakdown?: UsageBreakdown | null;
  delegated_total_tokens?: number | string | null;
  delegated_total_cost?: number | string | null;
  delegated_execution_count?: number | string | null;
  delegated_usage_breakdown?: UsageBreakdown | null;
  inclusive_total_tokens?: number | string | null;
  inclusive_total_cost?: number | string | null;
  inclusive_usage_breakdown?: UsageBreakdown | null;
};

type ConversationMessageLike = {
  role?: string | null;
};

type ConversationSessionLike = {
  id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  last_message_at?: string | null;
  message_count?: number | string | null;
  execution_count?: number | string | null;
  total_tokens?: number | string | null;
  total_cost?: number | string | null;
  usage_breakdown?: UsageBreakdown | null;
  delegated_total_tokens?: number | string | null;
  delegated_total_cost?: number | string | null;
  delegated_execution_count?: number | string | null;
  delegated_usage_breakdown?: UsageBreakdown | null;
  inclusive_total_tokens?: number | string | null;
  inclusive_total_cost?: number | string | null;
  inclusive_execution_count?: number | string | null;
  inclusive_usage_breakdown?: UsageBreakdown | null;
  messages?: ConversationMessageLike[];
  executions?: ConversationExecutionLike[];
};

interface ConversationDetailsButtonProps {
  sessionId?: string | null;
  sessionData?: ConversationSessionLike | null;
  messages?: ConversationMessageLike[];
  agentName?: string | null;
  modelName?: string | null;
  systemName?: string | null;
  statusLabel?: string | null;
  currentExecutionId?: string | null;
  liveSessionId?: string | null;
  liveTotalTokens?: number | string | null;
  liveTotalCost?: number | string | null;
  isExecuting?: boolean;
  disabled?: boolean;
  tooltip?: string;
  buttonSx?: SxProps<Theme>;
  iconSx?: SxProps<Theme>;
  iconButtonProps?: Record<string, any>;
}

const toFiniteNumber = (value: unknown): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const firstFiniteNumber = (...values: unknown[]): number | null => {
  for (const value of values) {
    if (value === undefined || value === null || value === '') continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
};

const formatCompactCurrency = (value: number): string => {
  const amount = toFiniteNumber(value);
  return `$${amount >= 1 ? amount.toFixed(2) : amount.toFixed(4)}`;
};

const formatMetricNumber = (value: number): string => (
  Math.round(toFiniteNumber(value)).toLocaleString()
);

const formatConversationDate = (value?: string | null): string => {
  if (!value) return 'Not available';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not available' : date.toLocaleString();
};

const formatShortId = (value?: string | null): string => {
  if (!value) return 'Not started';
  return value.length > 16 ? `${value.slice(0, 8)}...${value.slice(-4)}` : value;
};

const normalizeRole = (role?: string | null): string => String(role || '').toUpperCase();

export default function ConversationDetailsButton({
  sessionId,
  sessionData,
  messages = [],
  agentName,
  modelName,
  systemName,
  statusLabel,
  currentExecutionId,
  liveSessionId,
  liveTotalTokens,
  liveTotalCost,
  isExecuting = false,
  disabled = false,
  tooltip = 'Conversation details',
  buttonSx,
  iconSx,
  iconButtonProps,
}: ConversationDetailsButtonProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const metrics = useMemo(() => {
    const executions = Array.isArray(sessionData?.executions) ? sessionData.executions : [];
    const currentExecutionKey = currentExecutionId ? String(currentExecutionId) : null;
    const liveTokens = toFiniteNumber(liveTotalTokens);
    const liveCost = toFiniteNumber(liveTotalCost);

    let persistedTokens = 0;
    let persistedCost = 0;
    let persistedCurrentTokens = 0;
    let persistedCurrentCost = 0;
    let hasPersistedCurrentExecution = false;
    let runningExecutions = 0;
    let delegatedExecutions = 0;
    let persistedUsageBreakdown = mergeUsageBreakdowns();
    let persistedCurrentUsageBreakdown: UsageBreakdown | null = null;

    executions.forEach((execution) => {
      const executionTokens = toFiniteNumber(execution?.inclusive_total_tokens ?? execution?.total_tokens);
      const executionCost = toFiniteNumber(execution?.inclusive_total_cost ?? execution?.total_cost);
      const executionUsage = execution?.inclusive_usage_breakdown || mergeUsageBreakdowns(
        execution?.usage_breakdown,
        execution?.delegated_usage_breakdown,
      );
      const executionStatus = String(execution?.status || '').toUpperCase();
      delegatedExecutions += toFiniteNumber(execution?.delegated_execution_count);

      if (executionStatus === 'RUNNING' || executionStatus === 'PENDING_CONFIRMATION') {
        runningExecutions += 1;
      }

      if (currentExecutionKey && String(execution?.id) === currentExecutionKey) {
        hasPersistedCurrentExecution = true;
        persistedCurrentTokens = executionTokens;
        persistedCurrentCost = executionCost;
        persistedCurrentUsageBreakdown = executionUsage;
        return;
      }

      persistedTokens += executionTokens;
      persistedCost += executionCost;
      persistedUsageBreakdown = mergeUsageBreakdowns(persistedUsageBreakdown, executionUsage);
    });

    if (currentExecutionKey) {
      const displayedSessionKey = sessionId || sessionData?.id || null;
      const shouldUseLiveTotals = hasPersistedCurrentExecution
        || !displayedSessionKey
        || !liveSessionId
        || displayedSessionKey === liveSessionId;

      persistedTokens += shouldUseLiveTotals
        ? Math.max(persistedCurrentTokens, liveTokens)
        : persistedCurrentTokens;
      persistedCost += shouldUseLiveTotals
        ? Math.max(persistedCurrentCost, liveCost)
        : persistedCurrentCost;
      persistedUsageBreakdown = mergeUsageBreakdowns(
        persistedUsageBreakdown,
        persistedCurrentUsageBreakdown,
      );

      if (shouldUseLiveTotals && isExecuting && !hasPersistedCurrentExecution) {
        runningExecutions += 1;
      }
    }

    const sessionTokens = firstFiniteNumber(
      sessionData?.inclusive_total_tokens,
      sessionData?.total_tokens,
    );
    const sessionCost = firstFiniteNumber(
      sessionData?.inclusive_total_cost,
      sessionData?.total_cost,
    );
    const liveExecutionCount = currentExecutionKey && !hasPersistedCurrentExecution ? 1 : 0;
    const derivedExecutionCount = (
      Math.max(toFiniteNumber(sessionData?.execution_count), executions.length)
      + delegatedExecutions
      + liveExecutionCount
    );
    const sessionExecutionCount = firstFiniteNumber(sessionData?.inclusive_execution_count);

    const loadedMessages = messages.length;
    const messageCount = toFiniteNumber(sessionData?.message_count)
      || toFiniteNumber(sessionData?.messages?.length)
      || loadedMessages;
    const sessionUsageBreakdown = sessionData?.inclusive_usage_breakdown || mergeUsageBreakdowns(
      sessionData?.usage_breakdown,
      sessionData?.delegated_usage_breakdown,
    );
    const usageBreakdown = hasUsageBreakdown(sessionUsageBreakdown)
      ? sessionUsageBreakdown
      : persistedUsageBreakdown;

    return {
      totalTokens: Math.max(sessionTokens ?? 0, persistedTokens),
      totalCost: Math.max(sessionCost ?? 0, persistedCost),
      executionCount: Math.max(sessionExecutionCount ?? 0, derivedExecutionCount),
      runningExecutions,
      messageCount,
      loadedMessages,
      userMessages: messages.filter((message) => normalizeRole(message.role) === 'USER').length,
      assistantMessages: messages.filter((message) => normalizeRole(message.role) === 'ASSISTANT').length,
      toolCalls: messages.filter((message) => normalizeRole(message.role) === 'TOOL_CALL').length,
      usageBreakdown,
    };
  }, [
    currentExecutionId,
    isExecuting,
    liveSessionId,
    liveTotalCost,
    liveTotalTokens,
    messages,
    sessionData,
    sessionId,
  ]);

  const detailRows = useMemo(() => [
    { label: 'Session', value: formatShortId(sessionId || sessionData?.id || null) },
    { label: 'Agent', value: agentName || 'Not available' },
    { label: 'Model', value: modelName || 'Not set' },
    { label: 'System', value: systemName || 'Not available' },
    { label: 'Created', value: formatConversationDate(sessionData?.created_at) },
    { label: 'Last message', value: formatConversationDate(sessionData?.last_message_at || sessionData?.updated_at) },
    { label: 'Status', value: statusLabel || (sessionId ? 'Idle' : 'No active session') },
  ], [agentName, modelName, sessionData, sessionId, statusLabel, systemName]);

  return (
    <>
      <Tooltip title={tooltip}>
        <span>
          <IconButton
            size="small"
            onClick={handleOpen}
            onMouseDown={(event) => event.stopPropagation()}
            color={open ? 'primary' : 'default'}
            aria-label="Conversation details"
            disabled={disabled}
            sx={buttonSx}
            {...iconButtonProps}
          >
            <InfoOutlinedIcon sx={iconSx} />
          </IconButton>
        </span>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: 20000 }}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100vw - 32px)', sm: 380 },
            maxWidth: 'calc(100vw - 32px)',
            p: 2,
            borderRadius: 1,
            zIndex: 20001,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
              Conversation Details
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {sessionId || sessionData?.id
                ? `Session ${formatShortId(sessionId || sessionData?.id || null)}`
                : 'No persisted session yet'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} aria-label="Close conversation details">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1, mb: 1.5 }}>
          {[
            { label: 'Total cost', value: formatCompactCurrency(metrics.totalCost) },
            { label: 'Total tokens', value: formatMetricNumber(metrics.totalTokens) },
            { label: 'Messages', value: formatMetricNumber(metrics.messageCount) },
            { label: 'Executions', value: formatMetricNumber(metrics.executionCount) },
          ].map((metric) => (
            <Box key={metric.label} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 1, py: 0.75, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                {metric.label}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                {metric.value}
              </Typography>
            </Box>
          ))}
        </Box>

        <UsageBreakdownPanel
          usage={metrics.usageBreakdown}
          title="Usage breakdown"
          compact
          sx={{ mb: 1.5 }}
        />

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {detailRows.map((row) => (
            <Box key={row.label} sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {row.label}
              </Typography>
              <Typography variant="body2" sx={{ minWidth: 0, textAlign: 'right' }} noWrap>
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.75 }}>
          <Chip size="small" variant="outlined" label={`${metrics.userMessages} user`} />
          <Chip size="small" variant="outlined" label={`${metrics.assistantMessages} assistant`} />
          <Chip size="small" variant="outlined" label={`${metrics.toolCalls} tool calls`} />
          <Chip
            size="small"
            variant="outlined"
            color={metrics.runningExecutions > 0 ? 'warning' : 'default'}
            label={`${metrics.runningExecutions} running`}
          />
        </Box>

        {metrics.loadedMessages < metrics.messageCount && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Showing {metrics.loadedMessages.toLocaleString()} loaded messages from this conversation.
          </Typography>
        )}
      </Popover>
    </>
  );
}
